/**
 * Render a slash-command chat message as natural-language prose for the
 * chat bubble. Falls back to raw text only when the command is genuinely
 * unknown (typo); every registered slash should resolve to prose.
 *
 * Render-time only — the underlying message stays raw so the backend
 * (Tavian / enricher) keeps seeing what the user actually typed. The
 * goal here is purely "the bubble must never display `/cmd` text".
 *
 * Lookup order:
 *   1. Hand-crafted lobby phrasings (BUBBLE_TEMPLATES) — overrides anything
 *      below for "Invite Kael to the party"-style prose.
 *   2. `GAME_COMMANDS` registry — the same source the slash menu uses, so
 *      adding a new game command automatically picks up a humanized form.
 *      `textTemplate` is preferred (substitutes `{target}` / `{item}` /
 *      `{object}` / `{spell}` etc. with the typed args), `fillText` is the
 *      fallback for arg-less commands.
 *   3. `SLASH_COMMANDS` registry — covers inner-voice and lobby commands
 *      (e.g. `/portrait`, `/improve`).
 *   4. Capitalized command name + raw args — last-resort guard so even an
 *      unknown command never reaches the bubble as `/foo bar`.
 */

import { GAME_COMMANDS } from '@/components/panels/game-commands';
import { SLASH_COMMANDS } from '@/components/panels/chat-commands';

const BUBBLE_TEMPLATES: Record<string, (args: string) => string> = {
  invite: (args) => (args ? `Invite ${args} to the party` : 'Invite a character to the party'),
  leave: () => 'Leave this chat room',
  'form-party': (args) => (args ? `Form a party named ${args}` : 'Form a party'),
  duel: (args) => (args ? `Challenge ${args} to a practice duel` : 'Challenge to a practice duel'),
  'start-adventure': () => 'Start the adventure',
  clear: () => 'Clear chat history',
  feedback: () => 'Send feedback',
  // Tavian-chat shortcuts (lobby NL-rewrite shortcuts; see ChatPanel.tsx Tavian
  // branch). Each row mirrors the natural-language phrase the slash form
  // rewrites to before sendTavianMessage.
  'create-char': () => "I'd like to create a new character",
  'create-party': () => "I'd like to create a new party",
  'select-adventure': () => 'What adventures can we play?',
  'talk-to': (args) => (args ? `Take me to ${args}` : 'Who can I talk to?'),
  aldric: () => 'Take me to Aldric',
  kira: () => 'Take me to Master Kira',
  grolt: () => 'Take me to Grolt',
  help: () => 'What can I do here?',
  'release-character': () => "I'd like to release my character",
  'disband-party': () => "I'd like to disband my party",
  'level-up': () => "I'd like to level up",
  'who-is-here': () => "Who's here at camp?",
  'rename-party': () => 'Help me rename the party',
  equip: (args) => (args ? `Equip my ${args}` : 'What can I equip?'),
  sell: (args) => (args ? `How much will you give me for ${args}?` : 'What can I sell?'),
};

// Args are restricted to a single line (`[^\n]+`) so a multi-line paste that
// happens to start with `/cmd` doesn't render as natural-language prose with
// embedded newlines.
const SLASH_COMMAND_RE = /^\/([\w-]+)(?:[ \t]+([^\n]+))?[ \t]*$/;

const PLACEHOLDER_RE = /\{[^}]+\}/g;

function applyTextTemplate(template: string, args: string): string {
  const trimmedArgs = args.trim();
  const placeholders = template.match(PLACEHOLDER_RE) ?? [];
  const [first, ...rest] = placeholders;
  if (!first) {
    // No placeholder — append args defensively so a player who typed
    // `/dodge harder` still sees "I take the Dodge action harder" instead of
    // losing the trailing word silently. Rare, but cheap.
    return trimmedArgs ? `${template} ${trimmedArgs}`.trim() : template.trim();
  }
  // Substitute the first placeholder with the entire args string. Strip any
  // remaining placeholders — multi-arg slashes typed raw without going through
  // the menu can't be properly resolved here. After stripping, also drop
  // trailing connector words/punct that bound the now-removed placeholder,
  // so e.g. `/cast Fireball` renders "Cast Fireball" instead of "Cast Fireball
  // at" (template `Cast {spell} at {target}`), and `/say Goblin` renders
  // "Say to Goblin" instead of "Say to Goblin:" (template `Say to {target}: `).
  let result = template.replace(first, trimmedArgs);
  for (const ph of rest) {
    result = result.replace(ph, '');
  }
  result = result.replace(/\s+/g, ' ').trim();
  // Strip a trailing connector preposition (`at` / `to` / etc.) when the
  // word it bound is gone. Two cases produce a dangling preposition:
  //   1. A second placeholder was stripped after the connector — e.g.
  //      `/cast Fireball` against `Cast {spell} at {target}` leaves
  //      "Cast Fireball at".
  //   2. The first (and only) placeholder followed a literal connector
  //      and the user gave no args — e.g. `/sayto` (no target) against
  //      `Say to {target}: ` leaves "Say to" after the colon strip.
  // Both are bubble-quality bugs. Anything else (single-placeholder
  // template with no connector + no args) was already fine pre-strip.
  if (rest.length > 0 || !trimmedArgs) {
    result = result.replace(/\s+(at|to|on|with|from|as|by|of|in|into)\b[\s,:]*$/i, '');
  }
  // Always trim a dangling colon/comma at the end (covers the `/sayto` /
  // `/whisper` / `/givecoins` templates whose trailing ": " is meant for
  // the user to fill with a message; if no message was typed, the colon
  // dangles).
  result = result.replace(/[:,]+\s*$/, '').trim();
  return result;
}

function capitalize(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}

/**
 * Strip a leading "I " from text and capitalize the resulting verb so
 * the bubble reads as a third-person imperative. The chat already
 * attributes the line to the speaker, so the leading "I" is redundant
 * noise.
 *
 * The slash-command registries themselves now author imperative templates
 * ("Attack {target}", "Take the Dodge action"), so this is a defense-in-depth
 * safety net for any third-party / legacy command that still ships first-person
 * text, plus the inner-voice slash registry which has its own conventions.
 *
 * Only strips a literal `I ` at the very start — mid-sentence pronouns
 * ("Help me decide what to equip. I want to ") are left alone.
 */
function stripLeadingFirstPerson(s: string): string {
  if (!/^I\s+/.test(s)) return s;
  const rest = s.replace(/^I\s+/, '');
  return capitalize(rest);
}

export function renderSlashCommandText(text: string | null | undefined): string {
  if (!text) return text ?? '';
  const match = text.trim().match(SLASH_COMMAND_RE);
  if (!match) return text;
  const [, command, rawArgs] = match;
  const args = (rawArgs ?? '').trim().replace(/^["']|["']$/g, '');
  const cmdLower = command.toLowerCase();

  // 1. Hand-crafted lobby templates first.
  const legacy = BUBBLE_TEMPLATES[cmdLower];
  if (legacy) return legacy(args);

  // 2. Game-room registry (textTemplate or fillText).
  const gameCmd = GAME_COMMANDS.find((c) => c.name.toLowerCase() === cmdLower);
  if (gameCmd) {
    if (gameCmd.textTemplate) {
      return stripLeadingFirstPerson(applyTextTemplate(gameCmd.textTemplate, args));
    }
    if (gameCmd.fillText) {
      // Skip fillText that itself starts with a slash — `/feedback`'s fillText
      // is `/feedback ` (intentional, kept so the chat-panel parseSlashCommand
      // catches it before the intent pipeline). For display we want prose.
      if (!gameCmd.fillText.trim().startsWith('/')) {
        const fill = gameCmd.fillText.trim();
        const composed = args ? `${fill} ${args}`.trim() : fill;
        return stripLeadingFirstPerson(composed);
      }
    }
  }

  // 3. Inner-voice / shared-lobby slash registry.
  const slashCmd = SLASH_COMMANDS.find((c) => c.name.toLowerCase() === cmdLower);
  if (slashCmd?.fillText) {
    const fill = slashCmd.fillText;
    // Several inner-voice fillTexts intentionally end with a trailing space
    // (the menu fills the input and lets the user keep typing). Trim before
    // appending args so we don't get "Help me decide what to equip.  axe".
    const fillTrimmed = fill.replace(/\s+$/, '');
    const composed = args ? `${fillTrimmed} ${args}`.trim() : fillTrimmed;
    return stripLeadingFirstPerson(composed);
  }

  // 4. Last resort — never let `/cmd` reach the bubble. Capitalize the verb
  // and append the raw args. This catches typos and any future slash command
  // that lands without a template entry.
  const verb = capitalize(command);
  return args ? `${verb} ${args}` : verb;
}
